import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, TextField, InputAdornment, Chip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import CustomAvatar from "components/common/Avatar";
import Button from "components/common/Button";
import PageLoader from "components/common/PageLoader";
import { getEmployeesWithClockInStatus } from "api/clockInWorkforce";
import { baseUrl } from "util/APIBaseUrl";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "hooks/usePermission";
import "./WorkforceModal.scss";

const FILTER_OPTIONS = {
  all: { label: "All", statusTypes: [] },
  available: { label: "Available", statusTypes: ["clocked_in"], color: "#06B500" },
  on_break: { label: "On break", statusTypes: ["on_break"], color: "#EB730E" },
  out_of_office: {
    label: "Out of office",
    statusTypes: ["clocked_out", "on_leave", "not_clocked_in", "no_shift"],
    color: "#939393",
  },
};

const STATUS_CONFIG = {
  clocked_in: { color: "#06B500", label: "Available" },
  on_break: { color: "#EB730E", label: "On break" },
  clocked_out: { color: "#939393", label: "Out of office" },
  on_leave: { color: "#939393", label: "On leave" },
  not_activated: { color: "#939393", label: "Attendance not enabled" },
  not_clocked_in: { color: "#939393", label: "Not clocked in" },
  no_shift: { color: "#939393", label: "Shift not selected" },
};

function WorkforceModal({ open, onClose, customerId }) {
  const navigate = useNavigate();
  const canConfigureTracking = hasPermission("timeTracking.configure");
  const [employees, setEmployees] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getEmployeesWithClockInStatus(customerId, {
        page: 1,
        limit: 100,
      });

      if (response?.success) {
        setEmployees(response.data?.rows || []);
        setTotalCount(response.data?.count || 0);
      } else if (response?.rows) {
        setEmployees(response.rows || []);
        setTotalCount(response.count || 0);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error fetching workforce:", err);
      const message = err?.response?.data?.message || err?.message || "Failed to load workforce data";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (open) {
      fetchEmployees();
    }
  }, [open, fetchEmployees]);

  const getFilterCount = useCallback(
    (filterKey) => {
      const filter = FILTER_OPTIONS[filterKey];
      if (!filter || filter.statusTypes.length === 0) return employees.length;
      return employees.filter((emp) => filter.statusTypes.includes(emp.clockInStatus?.status)).length;
    },
    [employees]
  );

  const filteredEmployees = useMemo(() => {
    let result = employees;

    if (activeFilter !== "all") {
      const filter = FILTER_OPTIONS[activeFilter];
      if (filter) {
        result = result.filter((emp) => filter.statusTypes.includes(emp.clockInStatus?.status));
      }
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((employee) => {
        const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
        return fullName.includes(searchLower);
      });
    }

    return result;
  }, [employees, searchTerm, activeFilter]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleManageClockIn = () => {
    navigate("/main/settings?tab=clockin");
    onClose();
  };

  const getStatusInfo = (employee) => {
    if (!employee.clockInStatus?.isEnabled) {
      return { color: "transparent", message: "Attendance not activated" };
    }
    const status = employee.clockInStatus?.status;
    const config = STATUS_CONFIG[status];
    return {
      color: config?.color || "transparent",
      message: employee.clockInStatus?.statusMessage || config?.label || "",
    };
  };

  const renderFilters = () => (
    <div className="workforce-modal-filters">
      {Object.entries(FILTER_OPTIONS).map(([key, filter]) => {
        const isSelected = activeFilter === key;
        const count = getFilterCount(key);
        const label = key === "all" ? filter.label : `${filter.label} (${count})`;

        return (
          <Chip
            key={key}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                {filter.color && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: filter.color,
                      flexShrink: 0,
                    }}
                  />
                )}
                <span>{label}</span>
              </Box>
            }
            onClick={() => setActiveFilter(key)}
            sx={{
              backgroundColor: isSelected ? "#8139d0" : "#ededed",
              color: isSelected ? "#fff" : "inherit",
              fontFamily: "var(--font-family-paragraph)",
              fontSize: "14px",
              fontWeight: 500,
              height: "32px",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: isSelected ? "#7030b8" : "#e0e0e0",
              },
              "& .MuiChip-label": {
                padding: "0 12px",
              },
            }}
          />
        );
      })}
    </div>
  );

  const renderContent = () => {
    if (loading && employees.length === 0) {
      return (
        <div className="workforce-modal-loading">
          <PageLoader />
        </div>
      );
    }

    if (error) {
      return (
        <div className="workforce-modal-error">
          <p className="para-body-m-regular text-error">{error}</p>
        </div>
      );
    }

    if (filteredEmployees.length === 0) {
      return (
        <div className="workforce-modal-empty">
          <p className="para-body-m-regular text-headings-secondary">
            {searchTerm ? "No employees found matching your search" : "No employees found"}
          </p>
        </div>
      );
    }

    return (
      <div className="workforce-modal-list">
        {filteredEmployees.map((employee) => {
          const statusInfo = getStatusInfo(employee);
          return (
            <div key={employee.employeeId} className="workforce-modal-item">
              <div className="workforce-modal-info">
                <div className="workforce-modal-avatar-wrapper">
                  <CustomAvatar
                    src={
                      employee.pictureVersion
                        ? `${baseUrl}/employee/${employee.employeeId}/logo?id=${employee.pictureVersion}`
                        : ""
                    }
                    alt={`${employee.firstName} ${employee.lastName}`}
                    size="md"
                  />
                  {employee.clockInStatus?.isEnabled && (
                    <span className="workforce-modal-status-badge" style={{ backgroundColor: statusInfo.color }} />
                  )}
                </div>
                <p className="heading-h5-medium workforce-modal-name">
                  {employee.firstName} {employee.lastName}
                </p>
              </div>

              <div className="workforce-modal-status">
                <p className="para-body-s-regular text-headings-secondary">{statusInfo.message}</p>
              </div>

              <IconButton
                size="small"
                className="workforce-modal-chevron"
                onClick={() => navigate(`/main/details/${employee.employeeId}#timesheet`)}
              >
                <ChevronRightIcon />
              </IconButton>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      width="616px"
      title={
        <div className="clockin-modal-title">
          <DialogTitle title={`Your workforce today (${totalCount.toLocaleString()})`} />
        </div>
      }
      content={
        <DialogContent>
          <Box className="workforce-modal-content">
            <TextField
              placeholder="Search employee by name"
              value={searchTerm}
              onChange={handleSearchChange}
              variant="outlined"
              size="small"
              fullWidth
              className="workforce-modal-search"
              slotProps={{
                input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 24, color: "#999" }} />
                  </InputAdornment>
                ),
              },
              }}
            />

            {renderFilters()}

            {renderContent()}
          </Box>
        </DialogContent>
      }
      actions={
        <DialogActions>
          {canConfigureTracking && (
            <Button
              label="Manage attendance"
              variant="contained"
              color="secondary"
              size="large"
              onClick={handleManageClockIn}
            />
          )}
          <Button label="Done" variant="contained" color="primary" size="large" onClick={onClose} />
        </DialogActions>
      }
    />
  );
}

WorkforceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customerId: PropTypes.string.isRequired,
};

export default WorkforceModal;
